/**
 * Auto Repair - Inventory Management Page
 * Task 62: Complete inventory system with stock tracking
 */

import { useEffect, useState } from 'react';
import { useInventoryItems, useLowStockItems, useCreateInventoryItem, useUpdateInventoryItem, useDeleteInventoryItem, useCreateInventoryTransaction } from '@/hooks/useInventory';
import { InventoryItem, InventoryTransactionType } from '@/types/inventory';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Box, Plus, Pencil, Trash2, ArrowUpDown, Search } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { MainLayout } from "@/components/MainLayout";

export default function AutoRepairInventoryPage() {
  useEffect(() => {
    document.title = 'Estoque | Controle Financeiro';
  }, []);

  const { data: inventoryItems = [], isLoading } = useInventoryItems();
  const { data: lowStockItems = [] } = useLowStockItems();
  const createItem = useCreateInventoryItem();
  const updateItem = useUpdateInventoryItem();
  const deleteItem = useDeleteInventoryItem();
  const createTransaction = useCreateInventoryTransaction();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<InventoryItem>>({
    name: '',
    description: '',
    sku: '',
    category: '',
    quantity_in_stock: 0,
    min_stock_level: 0,
    unit: 'unit',
    cost_price: null,
    sell_price: null,
    location: '',
  });

  const [stockMovement, setStockMovement] = useState({
    transaction_type: 'purchase' as InventoryTransactionType,
    quantity: 0,
    unit_cost: 0,
    notes: '',
  });

  // Get unique categories
  const categories = Array.from(new Set(inventoryItems.map(item => item.category).filter(Boolean)));

  // Filter items
  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.sku?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleAddItem = () => {
    createItem.mutate(formData, {
      onSuccess: () => {
        setIsAddDialogOpen(false);
        resetForm();
      },
    });
  };

  const handleEditItem = () => {
    if (!selectedItem) return;
    updateItem.mutate(
      { id: selectedItem.id, updates: formData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          resetForm();
          setSelectedItem(null);
        },
      }
    );
  };

  const handleDeleteItem = (id: string) => {
    if (confirm('Tem certeza que deseja remover este item do estoque?')) {
      deleteItem.mutate(id);
    }
  };

  const handleStockMovement = () => {
    if (!selectedItem) return;
    createTransaction.mutate(
      {
        inventory_item_id: selectedItem.id,
        ...stockMovement,
      },
      {
        onSuccess: () => {
          setIsStockDialogOpen(false);
          setStockMovement({
            transaction_type: 'purchase',
            quantity: 0,
            unit_cost: 0,
            notes: '',
          });
          setSelectedItem(null);
        },
      }
    );
  };

  const openEditDialog = (item: InventoryItem) => {
    setSelectedItem(item);
    setFormData(item);
    setIsEditDialogOpen(true);
  };

  const openStockDialog = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsStockDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      category: '',
      quantity_in_stock: 0,
      min_stock_level: 0,
      unit: 'unit',
      cost_price: null,
      sell_price: null,
      location: '',
    });
  };

  if (isLoading) {
    return (
<MainLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando estoque...</div>
        </div>
      </div>
    
    </MainLayout>);
  }

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Box className="h-8 w-8" />
            Gerenciamento de Estoque
          </h1>
          <p className="text-muted-foreground">Controle de peças e produtos</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar Item ao Estoque</DialogTitle>
              <DialogDescription>Cadastre um novo item no inventário</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome do item"
                />
              </div>
              <div>
                <Label>SKU/Código</Label>
                <Input
                  value={formData.sku || ''}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="Código do produto"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Filtros, Óleo, Pneus"
                />
              </div>
              <div>
                <Label>Quantidade Inicial</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.quantity_in_stock || 0}
                  onChange={(e) => setFormData({ ...formData, quantity_in_stock: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Estoque Mínimo</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.min_stock_level || 0}
                  onChange={(e) => setFormData({ ...formData, min_stock_level: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Select value={formData.unit || 'unit'} onValueChange={(val) => setFormData({ ...formData, unit: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unit">Unidade</SelectItem>
                    <SelectItem value="kg">Quilograma</SelectItem>
                    <SelectItem value="liter">Litro</SelectItem>
                    <SelectItem value="meter">Metro</SelectItem>
                    <SelectItem value="box">Caixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Localização</Label>
                <Input
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Prateleira, corredor, etc."
                />
              </div>
              <div>
                <Label>Preço de Custo</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost_price || ''}
                  onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || null })}
                  placeholder="R$ 0,00"
                />
              </div>
              <div>
                <Label>Preço de Venda</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sell_price || ''}
                  onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || null })}
                  placeholder="R$ 0,00"
                />
              </div>
              <div className="col-span-2">
                <Label>Descrição</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição detalhada do item"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddItem}>Adicionar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Low Stock Alerts */}
      {lowStockItems.length > 0 && (
        <Card className="border-orange-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="h-5 w-5" />
              Alertas de Estoque Baixo ({lowStockItems.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {lowStockItems.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center justify-between text-sm">
                  <span>{item.name} {item.sku && `(${item.sku})`}</span>
                  <Badge variant="destructive">
                    {item.quantity_in_stock} {item.unit} - Faltam {item.shortage_quantity.toFixed(2)}
                  </Badge>
                </div>
              ))}
              {lowStockItems.length > 5 && (
                <p className="text-xs text-muted-foreground">+ {lowStockItems.length - 5} itens com estoque baixo</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat || ''}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle>Itens em Estoque ({filteredItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Item</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Estoque</TableHead>
                  <TableHead className="text-right">Mín.</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead className="text-right">Venda</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      Nenhum item encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => {
                    const isLowStock = item.quantity_in_stock <= item.min_stock_level;
                    return (
                  <MainLayout>
    <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {item.description && (
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{item.sku || '-'}</TableCell>
                        <TableCell>
                          {item.category && <Badge variant="outline">{item.category}</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={isLowStock ? 'text-orange-600 font-medium' : ''}>
                            {item.quantity_in_stock} {item.unit}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-sm">
                          {item.min_stock_level}
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.cost_price)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.sell_price)}</TableCell>
                        <TableCell className="text-sm">{item.location || '-'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openStockDialog(item)}
                              title="Registrar movimentação"
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(item)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteItem(item.id)}
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    
    </MainLayout>);
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Item</DialogTitle>
            <DialogDescription>Atualize as informações do item</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {/* Same form fields as Add Dialog */}
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>SKU/Código</Label>
              <Input
                value={formData.sku || ''}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              />
            </div>
            <div>
              <Label>Categoria</Label>
              <Input
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>
            <div>
              <Label>Estoque Mínimo</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.min_stock_level || 0}
                onChange={(e) => setFormData({ ...formData, min_stock_level: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Unidade</Label>
              <Select value={formData.unit || 'unit'} onValueChange={(val) => setFormData({ ...formData, unit: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit">Unidade</SelectItem>
                  <SelectItem value="kg">Quilograma</SelectItem>
                  <SelectItem value="liter">Litro</SelectItem>
                  <SelectItem value="meter">Metro</SelectItem>
                  <SelectItem value="box">Caixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Localização</Label>
              <Input
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div>
              <Label>Preço de Custo</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.cost_price || ''}
                onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || null })}
              />
            </div>
            <div>
              <Label>Preço de Venda</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.sell_price || ''}
                onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || null })}
              />
            </div>
            <div className="col-span-2">
              <Label>Descrição</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleEditItem}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Movimentação de Estoque</DialogTitle>
            <DialogDescription>
              {selectedItem?.name} - Estoque atual: {selectedItem?.quantity_in_stock} {selectedItem?.unit}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Movimentação</Label>
              <Select
                value={stockMovement.transaction_type}
                onValueChange={(val) => setStockMovement({ ...stockMovement, transaction_type: val as InventoryTransactionType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">Compra (Entrada)</SelectItem>
                  <SelectItem value="usage">Uso/Consumo (Saída)</SelectItem>
                  <SelectItem value="sale">Venda (Saída)</SelectItem>
                  <SelectItem value="adjustment">Ajuste</SelectItem>
                  <SelectItem value="return">Devolução (Entrada)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                step="0.01"
                value={stockMovement.quantity}
                onChange={(e) => setStockMovement({ ...stockMovement, quantity: parseFloat(e.target.value) || 0 })}
                placeholder="Quantidade movimentada"
              />
            </div>
            <div>
              <Label>Custo Unitário (opcional)</Label>
              <Input
                type="number"
                step="0.01"
                value={stockMovement.unit_cost || ''}
                onChange={(e) => setStockMovement({ ...stockMovement, unit_cost: parseFloat(e.target.value) || 0 })}
                placeholder="R$ 0,00"
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                value={stockMovement.notes}
                onChange={(e) => setStockMovement({ ...stockMovement, notes: e.target.value })}
                placeholder="Notas sobre a movimentação"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsStockDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleStockMovement}>Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  
    </MainLayout>);
}
